import { useState } from 'react';

import { EmployeeUserOption, useGetEmployeeUsers } from '~/hooks/apis/users/useGetEmployeeUsers';
import useDebounced from '~/hooks/useDebounced';

const SEARCH_DELAY_MS = 250;
// Below this length the search runs unfiltered, so "@" alone still lists people.
const MIN_FILTER_LENGTH = 2;
const MAX_SUGGESTIONS = 8;

function useMentionSearch(query: string | null) {
  const search = useDebounced(query?.trim() ?? '', SEARCH_DELAY_MS);
  const {
    data: employees,
    isFetching,
    isError,
  } = useGetEmployeeUsers(search.length >= MIN_FILTER_LENGTH ? search : undefined, query !== null);
  return { suggestions: (employees ?? []).slice(0, MAX_SUGGESTIONS), isFetching, isError };
}

interface NavigationOptions {
  suggestions: Array<EmployeeUserOption>;
  onSelect: (employee: EmployeeUserOption) => void;
  onDismiss: () => void;
}

function useMentionNavigation({ suggestions, onSelect, onDismiss }: NavigationOptions) {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Returns true when the key was consumed, so the caller can stop the input from also handling it.
  const handleKey = (event: { key: string; preventDefault: () => void }) => {
    if (suggestions.length === 0) {
      return false;
    }
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setHighlightedIndex((prev) => (prev + step + suggestions.length) % suggestions.length);
        break;
      }
      case 'Enter':
      case 'Tab':
        onSelect(suggestions[Math.min(highlightedIndex, suggestions.length - 1)]);
        break;
      case 'Escape':
        onDismiss();
        break;
      default:
        return false;
    }
    event.preventDefault();
    return true;
  };

  return { highlightedIndex, resetHighlight: () => setHighlightedIndex(0), handleKey };
}

interface FoundMention {
  // Identifies where the mention starts, so a picked one can be told apart from the next.
  id: string;
  query: string;
}

// `found` is the mention being typed at the caret, or null when there isn't one.
export function useMentionSession(found: FoundMention | null, insertMention: (employee: EmployeeUserOption) => void) {
  const [isDismissed, setIsDismissed] = useState(false);
  // A picked mention stays closed so the space after it isn't read as more query.
  const [pickedId, setPickedId] = useState<string | null>(null);

  const active = isDismissed || found?.id === pickedId ? null : found;
  const { suggestions, isFetching, isError } = useMentionSearch(active?.query ?? null);
  const isOpen = active !== null && (suggestions.length > 0 || isFetching || isError);

  const selectEmployee = (employee: EmployeeUserOption) => {
    if (found) {
      setPickedId(found.id);
    }
    insertMention(employee);
  };

  const { highlightedIndex, resetHighlight, handleKey } = useMentionNavigation({
    suggestions,
    onSelect: selectEmployee,
    onDismiss: () => setIsDismissed(true),
  });

  return {
    isOpen,
    suggestions,
    isError,
    highlightedIndex,
    selectEmployee,
    handleKey: (event: { key: string; preventDefault: () => void }) => isOpen && handleKey(event),
    dismiss: () => setIsDismissed(true),
    reopen: () => {
      setIsDismissed(false);
      resetHighlight();
    },
  };
}
